import { useMemo } from 'react'

import { sumBy } from 'lodash'
import { formatUnits } from 'viem'
import { base } from 'viem/chains'

import {
  getUpgradeScenario,
  UpgradeAccount,
  UpgradeScenario,
} from '~/app/settings/team/upgrade/utils'
import { REFETCH_ORG_PLAN_UPGRADE_PAGE } from '~/constants/refetch-intervals'
import { useActiveOrgId } from '~/hooks/org'
import { useOrgBalances } from '~/hooks/useBalances'
import { useOrgPlan } from '~/hooks/useOrgPlan'
import { PaidPlanName, Plan, usePlans } from '~/hooks/usePlans'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { SmartAccountRole } from '~/types'
import { Address, EvmAddress } from '~/utils/address'
import { meetsPlan } from '~/utils/plans'
import { BASE_USDC, isLegacyVault, isVaultToken } from '~/utils/vault'

const PREVIEW_ADDRESSES = [
  EvmAddress.format('0x1111111111111111111111111111111111111111'),
  EvmAddress.format('0x2222222222222222222222222222222222222222'),
]

const PREVIEW_STATES: Record<
  UpgradeScenario,
  { amountNeeded: number; accounts: UpgradeAccount[] }
> = {
  satisfied: { amountNeeded: 0, accounts: [] },
  single: {
    amountNeeded: 2500,
    accounts: [
      {
        address: PREVIEW_ADDRESSES[0],
        name: 'Treasury',
        usdcBalance: 8000,
        depositAmount: 2500,
      },
    ],
  },
  multi: {
    amountNeeded: 2500,
    accounts: [
      {
        address: PREVIEW_ADDRESSES[0],
        name: 'Treasury',
        usdcBalance: 1500,
        depositAmount: 1500,
      },
      {
        address: PREVIEW_ADDRESSES[1],
        name: 'Operating',
        usdcBalance: 1200,
        depositAmount: 1000,
      },
    ],
  },
  insufficient: { amountNeeded: 2500, accounts: [] },
}

// Treasury and operating accounts are the ones the Treasury can deposit from.
const DEPOSIT_ROLES = [SmartAccountRole.TREASURY, SmartAccountRole.OPERATING]

// Compares the org's Earn balance (any network, excluding legacy vaults) against
// the plan's earnMinimum and classifies how the shortfall can be funded from
// Base USDC. previewScenario swaps live balances for fixtures so each state can
// be viewed without funds.
export const useUpgradeState = ({
  planName,
  previewScenario,
}: {
  planName: PaidPlanName
  previewScenario?: UpgradeScenario
}): {
  plan: Plan | undefined
  scenario: UpgradeScenario | undefined
  amountNeeded: number | undefined
  /** The org's Earn balance is what holds its plan, not a subscription or override */
  isEarnBacked: boolean
  accounts: UpgradeAccount[]
  isLoading: boolean
  errorMessage: string | undefined
  retry: () => void
} => {
  const activeOrgId = useActiveOrgId()
  const {
    balances,
    isLoading: isLoadingBalances,
    isError: hasBalancesError,
    refetch: refetchBalances,
  } = useOrgBalances(activeOrgId)
  const { smartAccounts, isLoading: isLoadingAccounts } = useSmartAccounts()
  const { plans, isLoading, isFetching, isError, refetch } = usePlans()
  const plan = plans.find((p) => p.name === planName)
  const {
    data: orgPlan,
    isLoading: isLoadingOrgPlan,
    isError: hasOrgPlanError,
    refetch: refetchOrgPlan,
  } = useOrgPlan({ refetchInterval: REFETCH_ORG_PLAN_UPGRADE_PAGE })
  // The server decides whether the plan is held: subscriptions and admin
  // overrides qualify without Earn, and it tolerates the vault's share rounding
  // (a deposit of exactly the shortfall can value a cent short).
  const hasPlan = meetsPlan({
    plans,
    current: orgPlan?.plan,
    required: planName,
  })

  const currentEarnAmount = useMemo(
    () =>
      sumBy(Object.values(balances), (account) =>
        sumBy(
          account.balances.filter(
            (balance) =>
              isVaultToken(balance.chainId, balance.address) &&
              !isLegacyVault(balance.address),
          ),
          'usdValue',
        ),
      ),
    [balances],
  )
  const depositAccounts = useMemo(
    () =>
      smartAccounts
        .filter(
          (account) =>
            !account.isArchived && DEPOSIT_ROLES.includes(account.role),
        )
        .map((account) => ({
          address: EvmAddress.format(account.address),
          name: account.name,
          usdcBalance: sumBy(
            balances[account.address]?.balances.filter(
              (balance) =>
                balance.chainId === base.id &&
                Address.equal(balance.address, BASE_USDC),
            ),
            (balance) =>
              Number(
                formatUnits(balance.amount, balance.tokenMetadata.decimals),
              ),
          ),
        })),
    [smartAccounts, balances],
  )
  const liveAmountNeeded =
    !activeOrgId ||
    !plan ||
    isLoadingBalances ||
    isLoadingAccounts ||
    isLoadingOrgPlan ||
    hasBalancesError ||
    hasOrgPlanError
      ? undefined
      : hasPlan
        ? 0
        : Math.ceil(Math.max(plan.earnMinimum - currentEarnAmount, 0) * 100) /
          100
  const live = useMemo(
    () =>
      liveAmountNeeded === undefined
        ? undefined
        : getUpgradeScenario({
            amountNeeded: liveAmountNeeded,
            accounts: depositAccounts,
          }),
    [liveAmountNeeded, depositAccounts],
  )

  const preview = previewScenario && PREVIEW_STATES[previewScenario]

  return {
    plan,
    scenario: previewScenario ?? live?.scenario,
    amountNeeded: preview ? preview.amountNeeded : liveAmountNeeded,
    isEarnBacked: preview ? preview.amountNeeded === 0 : !!orgPlan?.earnBacked,
    accounts: preview ? preview.accounts : (live?.accounts ?? []),
    isLoading:
      !previewScenario &&
      ((!plan && isFetching) ||
        isLoadingBalances ||
        isLoadingAccounts ||
        isLoadingOrgPlan),
    errorMessage:
      (!plan && (isError || !isLoading)) ||
      (hasOrgPlanError && !previewScenario)
        ? 'Unable to load plan details.'
        : hasBalancesError && !previewScenario
          ? 'Unable to load balances.'
          : undefined,
    retry: () => {
      void refetch()
      void refetchBalances()
      void refetchOrgPlan()
    },
  }
}
